import {
  isFieldErrorResponse,
  type SimplePackagingSpecification,
  useApiTradeV1PackagingSpecificationsUpdate,
} from 'api';
import { useNotification } from 'features';
import { Form, useForm, yupResolver } from 'form';
import { useTranslation } from 'i18n';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from 'ui';

import {
  PackagingSpecificationInputType,
  PackagingSpecificationSchema,
  PackagingSpecificationType,
} from '../../pages/schema';
import { PackagingSpecificationForm } from '../PackackingSpecificationForm/PackagingSpecificationForm';

type Props = {
  open: boolean;
  onClose: () => void;
  packagingSpecification: SimplePackagingSpecification;
  onSuccess: () => void;
};

const convertSimplePackagingSpecificationToFormValues = (
  packagingSpecification: SimplePackagingSpecification
): PackagingSpecificationType => {
  return {
    density_unit: packagingSpecification.density_unit,
    dimension_unit: packagingSpecification.dimension_unit,
    max_density: packagingSpecification.max_density ? Number(packagingSpecification.max_density) : undefined,
    max_height: packagingSpecification.max_height ? Number(packagingSpecification.max_height) : undefined,
    max_length: packagingSpecification.max_length ? Number(packagingSpecification.max_length) : undefined,
    max_load_weight: packagingSpecification.max_load_weight
      ? Number(packagingSpecification.max_load_weight)
      : undefined,
    max_weight: packagingSpecification.max_weight ? Number(packagingSpecification.max_weight) : undefined,
    max_width: packagingSpecification.max_width ? Number(packagingSpecification.max_width) : undefined,
    min_density: packagingSpecification.min_density ? Number(packagingSpecification.min_density) : undefined,
    min_height: packagingSpecification.min_height ? Number(packagingSpecification.min_height) : undefined,
    min_length: packagingSpecification.min_length ? Number(packagingSpecification.min_length) : undefined,
    min_load_weight: packagingSpecification.min_load_weight
      ? Number(packagingSpecification.min_load_weight)
      : undefined,
    min_weight: packagingSpecification.min_weight ? Number(packagingSpecification.min_weight) : undefined,
    min_width: packagingSpecification.min_width ? Number(packagingSpecification.min_width) : undefined,
    packed_on: packagingSpecification.packed_on || undefined,
    storage_type: packagingSpecification.storage_type,
    strapping: packagingSpecification.strapping,
    title: packagingSpecification.title,
  };
};

export const UpdatePackagingSpecificationDialog = ({ open, onClose, packagingSpecification, onSuccess }: Props) => {
  const { t } = useTranslation('orders');
  const form = useForm<PackagingSpecificationInputType, unknown, PackagingSpecificationType>({
    defaultValues: convertSimplePackagingSpecificationToFormValues(packagingSpecification),
    resolver: yupResolver(PackagingSpecificationSchema),
  });
  const { open: openNotification } = useNotification();
  const { trigger } = form;

  const onDialogClose = () => {
    onClose();
  };

  const { mutate: updatePackagingSpecification, isPending } = useApiTradeV1PackagingSpecificationsUpdate({
    mutation: {
      onError: (error) => {
        if (isFieldErrorResponse(error)) {
          Object.keys(error.response?.data).forEach((field) => {
            form.setError(field as keyof PackagingSpecificationType, { message: error.response.data[field] });
          });
          if (error.response?.data.non_field_errors) {
            openNotification?.({
              description: error?.response?.data.non_field_errors[0],
              message: t('buyerMaterialSpecifications.updatePackagingSpecificationDialog.notifications.updateError'),
              type: 'error',
            });
          }
          return;
        }
        openNotification?.({
          message: t('buyerMaterialSpecifications.updatePackagingSpecificationDialog.notifications.updateError'),
          type: 'error',
        });
      },
      onSuccess: () => {
        onSuccess();
        onDialogClose();
        openNotification?.({
          message: t('buyerMaterialSpecifications.updatePackagingSpecificationDialog.notifications.updateSuccess'),
          type: 'success',
        });
      },
    },
  });

  const onSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) {
      return;
    }
    const values = form.getValues();
    updatePackagingSpecification({
      data: {
        density_unit: values.density_unit,
        dimension_unit: values.dimension_unit,
        max_density: values.max_density?.toString(),
        max_height: values.max_height?.toString(),
        max_length: values.max_length?.toString(),
        max_load_weight: values.max_load_weight?.toString(),
        max_weight: values.max_weight?.toString(),
        max_width: values.max_width?.toString(),
        min_density: values.min_density?.toString(),
        min_height: values.min_height?.toString(),
        min_length: values.min_length?.toString(),
        min_load_weight: values.min_load_weight?.toString(),
        min_weight: values.min_weight?.toString(),
        min_width: values.min_width?.toString(),
        packed_on: values.packed_on,
        storage_type: values.storage_type,
        strapping: values.strapping,
        title: values.title,
      },
      packagingSpecificationId: packagingSpecification.id,
    });
  };

  return (
    <Dialog open={open} onClose={onDialogClose}>
      <DialogTitle>{t('buyerMaterialSpecifications.updatePackagingSpecificationDialog.title')}</DialogTitle>
      <DialogContent>
        <Form<PackagingSpecificationInputType, unknown, PackagingSpecificationType> {...form} onSubmit={onSubmit}>
          <PackagingSpecificationForm />
        </Form>
      </DialogContent>
      <DialogActions>
        <Button variant="secondary" onClick={onClose}>
          {t('buyerMaterialSpecifications.updatePackagingSpecificationDialog.actions.cancel')}
        </Button>
        <Button
          loading={isPending}
          onClick={() => onSubmit()}
          aria-label={t('buyerMaterialSpecifications.updatePackagingSpecificationDialog.actions.updateAriaLabel')}
        >
          {t('buyerMaterialSpecifications.updatePackagingSpecificationDialog.actions.update')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
